import { log } from '../utils/logger';
import { sessionManager } from '../utils/sessionManager';

/**
 * Base API handler with consistent error handling and response formatting
 */

/**
 * Standard API response format
 * @typedef {Object} APIResponse
 * @property {*} data - The response data
 * @property {Error|null} error - Any error that occurred
 * @property {boolean} success - Whether the operation was successful
 */

/**
 * Wraps an API call with consistent error handling
 * @param {Function} apiCall - The API function to call
 * @param {string} operation - Description of the operation for error messages
 * @returns {Promise<APIResponse>}
 */
export const handleAPICall = async (apiCall, operation = 'API call') => {
  try {
    const response = await apiCall();
    
    // Handle Supabase response format
    if (response && typeof response === 'object') {
      if ('error' in response && response.error) {
        log.error(`❌ ${operation} failed:`, response.error);
        
        // Check if this is a session expiration error
        sessionManager.handlePotentialSessionExpiration(response.error);
        
        return {
          data: null,
          error: response.error,
          success: false
        };
      }
      
      if ('data' in response) {
        return {
          data: response.data,
          error: null,
          success: true
        };
      }
    }
    
    // Handle direct data response
    return {
      data: response,
      error: null,
      success: true
    };
  } catch (error) {
    log.error(`❌ ${operation} error:`, error);
    
    // Check if this is a session expiration error
    sessionManager.handlePotentialSessionExpiration(error);
    
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
      success: false
    };
  }
};

/**
 * Creates a standard API method with loading state management
 * @param {Function} apiCall - The API function to wrap
 * @param {Object} options - Configuration options
 * @returns {Function} Wrapped API method
 */
export const createAPIMethod = (apiCall, options = {}) => {
  const { 
    operation = 'API operation',
    onSuccess,
    onError,
    transformData
  } = options;
  
  return async (...args) => {
    const startTime = Date.now();
    log.info(`🔄 Starting ${operation}...`);
    
    try {
      const response = await handleAPICall(
        () => apiCall(...args),
        operation
      );
      
      if (response.success) {
        const transformedData = transformData 
          ? transformData(response.data) 
          : response.data;
          
        log.info(`✅ ${operation} completed in ${Date.now() - startTime}ms`);
        
        if (onSuccess) {
          onSuccess(transformedData);
        }
        
        return {
          ...response,
          data: transformedData
        };
      } else {
        if (onError) {
          onError(response.error);
        }
        return response;
      }
    } catch (error) {
      log.error(`❌ ${operation} failed:`, error);
      if (onError) {
        onError(error);
      }
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
        success: false
      };
    }
  };
};

/**
 * Creates a paginated API method
 * @param {Function} apiCall - The API function to wrap
 * @param {Object} options - Configuration options
 * @returns {Function} Wrapped API method with pagination
 */
export const createPaginatedAPIMethod = (apiCall, options = {}) => {
  const { 
    operation = 'Paginated API operation',
    pageSize = 20
  } = options;
  
  return async (page = 1, filters = {}) => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    return createAPIMethod(
      () => apiCall({ ...filters, from, to }),
      { operation: `${operation} (page ${page})` }
    )();
  };
};

/**
 * Batch multiple API calls with error handling
 * @param {Array<Function>} apiCalls - Array of API functions to call
 * @param {Object} options - Configuration options
 * @returns {Promise<Array<APIResponse>>}
 */
export const batchAPICall = async (apiCalls, options = {}) => {
  const { 
    parallel = true,
    stopOnError = false 
  } = options;
  
  if (parallel) {
    const results = await Promise.allSettled(
      apiCalls.map(call => handleAPICall(call))
    );
    
    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        log.error(`Batch API call ${index} failed:`, result.reason);
        return {
          data: null,
          error: result.reason,
          success: false
        };
      }
    });
  } else {
    const results = [];
    
    for (const [index, apiCall] of apiCalls.entries()) {
      const result = await handleAPICall(apiCall);
      results.push(result);
      
      if (!result.success && stopOnError) {
        log.error(`Batch API stopped at call ${index} due to error`);
        break;
      }
    }
    
    return results;
  }
};

/**
 * Retry an API call with exponential backoff
 * @param {Function} apiCall - The API function to call
 * @param {Object} options - Configuration options
 * @returns {Promise<APIResponse>}
 */
export const retryAPICall = async (apiCall, options = {}) => {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    operation = 'API call'
  } = options;
  
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), maxDelay);
      log.info(`⏳ Retrying ${operation} in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    const result = await handleAPICall(apiCall, operation);
    
    if (result.success) {
      if (attempt > 0) {
        log.info(`✅ ${operation} succeeded after ${attempt + 1} attempts`);
      }
      return result;
    }
    
    lastError = result.error;
  }
  
  log.error(`❌ ${operation} failed after ${maxRetries} attempts`);
  return {
    data: null,
    error: lastError || new Error(`${operation} failed after ${maxRetries} attempts`),
    success: false
  };
};